import { AppData, Lightning, Registry } from '@lightningjs/sdk';
import FocusableIcon from 'components/common/FocusableIcon';
import {
  debounceAnnounce,
  getImageTextureObj,
  preloadTextures,
} from 'support/generalUtils';
import { translate } from 'support/translate';

export const PLAY_ICON = 'static/images/playback/player-play-icon.svg';
export const PAUSE_ICON = 'static/images/playback/player-pause-icon.svg';
export const RE_10S_ICON = 'static/images/playback/re-10s-icon.svg';
export const FF_10S_ICON = 'static/images/playback/ff-10s-icon.svg';

const CC_ICON_SIZE = 48;
const CC_AUDIO_ICON = getImageTextureObj(
  'static/images/playback/audio-cc-icon-unfocused.svg',
  CC_ICON_SIZE,
  CC_ICON_SIZE,
);

export const CcAudioButton = {
  type: FocusableIcon,
  title: translate('ttsPrompts.closedCaption'),
  icon: CC_AUDIO_ICON,
  action: '$ccAudio',
};

const CONTROL_W = 368;
const CONTROL_H = 48;
const DEFAULT_ICON_SIZE = 48;

export const DEFAULT_SEEK_RATE = 1;
export const DEFAULT_SEEK_SPEED = 0;
export const SKIP_INTERVAL = 10;
export const SCRUB_RATE = 60;
export const SCRUB_DELAY = 1000;

const ANNOUNCE_SEEKING_INTERVAL = 5000;

export interface CommonPlaybackControlsTemplateSpec
  extends Lightning.Component.TemplateSpec {
  SkipBackward: typeof FocusableIcon;
  PlayPauseIcon: typeof FocusableIcon;
  SkipForward: typeof FocusableIcon;
}

export class CommonPlaybackControls
  extends Lightning.Component<CommonPlaybackControlsTemplateSpec>
  implements
    Lightning.Component
      .ImplementTemplateSpec<CommonPlaybackControlsTemplateSpec>
{
  protected _SkipBackward = this.getByRef('SkipBackward')!;
  protected _PlayPauseIcon = this.getByRef('PlayPauseIcon')!;
  protected _SkipForward = this.getByRef('SkipForward')!;

  private _seekInterval: number | null = null;
  private _seekAnnounceInterval: number | null = null;
  private _previousSeekDirection: 'fastForward' | 'rewind' = 'fastForward';

  getSeekRate: () => number = () => DEFAULT_SEEK_RATE;
  getSeekSpeed: () => number = () => DEFAULT_SEEK_SPEED;
  getInitialPlayPauseState: () => void = () => undefined;
  setRateSpeed: (rate: number, speed: number) => void = () => undefined;
  finishSeeking: () => void = () => undefined;
  onIncrementSeek: () => void = () => undefined;

  static override _template(): Lightning.Component.Template<CommonPlaybackControlsTemplateSpec> {
    return {
      w: CONTROL_W,
      h: CONTROL_H,
      mountX: 0.5,
      flex: {
        justifyContent: 'space-between',
        alignItems: 'center',
      },
      SkipBackward: {
        type: FocusableIcon,
        title: translate('ttsPrompts.skipBackwards'),
        icon: RE_10S_ICON,
        h: DEFAULT_ICON_SIZE,
        w: DEFAULT_ICON_SIZE,
        action: '$skipBack',
        signals: {
          $skipBack: true,
          $onHover: '$onHover',
        },
      },
      PlayPauseIcon: {
        type: FocusableIcon,
        title: translate('ttsPrompts.playButton'),
        icon: PLAY_ICON,
        h: DEFAULT_ICON_SIZE,
        w: DEFAULT_ICON_SIZE,
        action: '$playPause',
        signals: {
          $playPause: true,
          $onHover: '$onHover',
        },
      },
      SkipForward: {
        type: FocusableIcon,
        title: translate('ttsPrompts.skipForwards'),
        icon: FF_10S_ICON,
        h: DEFAULT_ICON_SIZE,
        w: DEFAULT_ICON_SIZE,
        action: '$skipForward',
        signals: {
          $skipForward: true,
          $onHover: '$onHover',
        },
      },
    };
  }

  get deviceSeekRates() {
    return AppData!.device.getSeekRate();
  }

  onFastForward() {
    this.startSeekSpeed('fastForward');
  }

  onRewind() {
    this.startSeekSpeed('rewind');
  }

  clearSeekInterval() {
    if (this._seekInterval) {
      Registry.clearInterval(this._seekInterval);
      this._seekInterval = null;
    }
  }

  clearSeekAnnounceInterval() {
    if (this._seekAnnounceInterval) {
      Registry.clearInterval(this._seekAnnounceInterval);
      this._seekAnnounceInterval = null;
    }
  }

  setupAnnounceInterval(parent: Lightning.Component) {
    if (AppData!.device.shouldAnnounceSeeking()) {
      this.announceSeeking(parent);
      // Wait for current announcements (progressBar + initial announceSeeking),
      // then set up 5 seconds interval
      this.stage.once<any>('announceEnded', () => {
        if (!this._seekAnnounceInterval) {
          this._seekAnnounceInterval = Registry.setInterval(() => {
            this.announceSeeking(parent);
          }, ANNOUNCE_SEEKING_INTERVAL);
        }
      });
    }
  }

  preloadIcons() {
    preloadTextures(this, [
      getImageTextureObj(PLAY_ICON, DEFAULT_ICON_SIZE, DEFAULT_ICON_SIZE),
      getImageTextureObj(PAUSE_ICON, DEFAULT_ICON_SIZE, DEFAULT_ICON_SIZE),
      getImageTextureObj(RE_10S_ICON, DEFAULT_ICON_SIZE, DEFAULT_ICON_SIZE),
      getImageTextureObj(FF_10S_ICON, DEFAULT_ICON_SIZE, DEFAULT_ICON_SIZE),
      CC_AUDIO_ICON,
    ]);
  }

  private announceSeeking(parent: Lightning.Component) {
    const rate = this.getSeekRate();
    const speed = this.getSeekSpeed();
    if (speed === 0) {
      this.clearSeekAnnounceInterval();
      return;
    }
    debounceAnnounce(
      parent,
      translate(
        `ttsPrompts.${rate > 0 ? 'fastForward' : 'rewind'}`,
        Math.abs(speed).toString(),
      ),
    );
  }

  private startSeekSpeed(direction: 'fastForward' | 'rewind') {
    if (this._previousSeekDirection !== direction) {
      this.clearSeekInterval();
      this.setRateSpeed(DEFAULT_SEEK_RATE, DEFAULT_SEEK_SPEED);
    }
    this._previousSeekDirection = direction;
    const seekDirection = direction === 'fastForward' ? 1 : -1;

    this.getInitialPlayPauseState();

    const currentRate = this.getSeekRate();
    const currentIndex = this.deviceSeekRates.indexOf(Math.abs(currentRate));

    // use circular indexing
    const nextIndex = (currentIndex + 1) % this.deviceSeekRates.length;

    const newSeekSpeed = nextIndex + 1;
    const newSeekRate = this.deviceSeekRates[nextIndex]! * seekDirection;

    this.setRateSpeed(newSeekRate, newSeekSpeed);

    if (!this._seekInterval) {
      this._seekInterval = Registry.setInterval(
        () => this.onIncrementSeek(),
        1000,
      );
    }
  }

  public setStepButtonsVisibility(visible: boolean) {
    this._SkipForward.visible = visible;
    this._SkipBackward.visible = visible;

    this.flex.justifyContent = visible ? 'space-between' : 'center';
  }

  private $skipBack() {
    this.signal('SkipBack');
  }

  private $playPause() {
    this.signal('PlayPause');
  }

  private $skipForward() {
    this.signal('SkipForward');
  }

  private $onHover(target: Lightning.Component) {
    this.signal('onHover', target);
  }
}
